import { createInstance, isUndefined, merge, extend, getValue } from './util';
/**
 * Returns the Class Object
 * @param {ClassObject} instance - instance of ClassObject
 * @param {string} curKey - key of the current instance
 * @param {Object} defaultValue - default Value
 * @param {Object[]} type
 */

function getObject(instance, curKey, defaultValue, type) {
  if (!instance.properties.hasOwnProperty(curKey) || !(instance.properties[curKey] instanceof type)) {
    instance.properties[curKey] = createInstance(type, [instance, curKey, defaultValue]);
  }

  return instance.properties[curKey];
}
/**
 * Returns object array
 * @param {ClassObject} instance
 * @param {string} curKey
 * @param {Object[]} defaultValue
 * @param type
 * @param {boolean} isSetter
 * @returns {Object[]}
 */


function getObjectArray(instance, curKey, defaultValue, type, isSetter, isFactory) {
  var result = [];
  var len = defaultValue ? defaultValue.length : 0;

  for (var i = 0; i < len; i++) {
    var curType = type;

    if (isFactory) {
      curType = type(defaultValue[i], instance);
    }

    if (isSetter) {
      var inst = createInstance(curType, [instance, curKey, {}, true]);
      inst.setProperties(defaultValue[i], true);
      result.push(inst);
    } else {
      result.push(createInstance(curType, [instance, curKey, defaultValue[i], false]));
    }
  }

  return result;
}
/**
 * Returns the properties of the object
 * @param {Object} defaultValue
 * @param {string} curKey
 */


function propertyGetter(defaultValue, curKey) {
  return function () {
    if (!this.properties.hasOwnProperty(curKey)) {
      this.properties[curKey] = defaultValue;
    }

    return this.properties[curKey];
  };
}
/**
 * Set the properties for the object
 * @param {Object} defaultValue
 * @param {string} curKey
 */


function propertySetter(defaultValue, curKey) {
  return function (newValue) {
    if (this.properties[curKey] !== newValue) {
      var oldVal = this.properties.hasOwnProperty(curKey) ? this.properties[curKey] : defaultValue;
      this.saveChanges(curKey, newValue, oldVal);
      this.properties[curKey] = newValue;
    }
  };
}
/**
 * Returns complex objects
 */


function complexGetter(defaultValue, curKey, type) {
  return function () {
    return getObject(this, curKey, defaultValue, type);
  };
}
/**
 * Sets complex objects
 */


function complexSetter(defaultValue, curKey, type) {
  return function (newValue) {
    getObject(this, curKey, defaultValue, type).setProperties(newValue);
  };
}

function complexFactoryGetter(defaultValue, curKey, type) {
  return function () {
    var curType = type({});

    if (this.properties.hasOwnProperty(curKey)) {
      return this.properties[curKey];
    } else {
      return getObject(this, curKey, defaultValue, curType);
    }
  };
}

function complexFactorySetter(defaultValue, curKey, type) {
  return function (newValue) {
    var curType = type(newValue, this);
    getObject(this, curKey, defaultValue, curType).setProperties(newValue);
  };
}

function complexArrayGetter(defaultValue, curKey, type) {
  return function () {
    var _this = this;

    if (!this.properties.hasOwnProperty(curKey)) {
      var defCollection = getObjectArray(this, curKey, defaultValue, type, false);
      this.properties[curKey] = defCollection;
    }

    var ignore = this.controlParent !== undefined && this.controlParent.ignoreCollectionWatch || this.ignoreCollectionWatch;

    if (!this.properties[curKey].hasOwnProperty('push') && !ignore) {
      ['push', 'pop'].forEach(function (extendFunc) {
        var descriptor = {
          value: complexArrayDefinedCallback(extendFunc, curKey, type, _this.properties[curKey]).bind(_this),
          configurable: true
        };
        Object.defineProperty(_this.properties[curKey], extendFunc, descriptor);
      });
    }

    if (!this.properties[curKey].hasOwnProperty('isComplexArray')) {
      Object.defineProperty(this.properties[curKey], 'isComplexArray', {
        value: true
      });
    }

    return this.properties[curKey];
  };
}

function complexArraySetter(defaultValue, curKey, type) {
  return function (newValue) {
    this.isComplexArraySetter = true;
    var oldValueCollection = getObjectArray(this, curKey, defaultValue, type, false);
    var newValCollection = getObjectArray(this, curKey, newValue, type, true);
    this.isComplexArraySetter = false;
    this.saveChanges(curKey, newValCollection, oldValueCollection);
    this.properties[curKey] = newValCollection;
  };
}

function complexArrayFactorySetter(defaultValue, curKey, type) {
  return function (newValue) {
    var oldValueCollection = this.properties.hasOwnProperty(curKey) ? this.properties[curKey] : defaultValue;
    var newValCollection = getObjectArray(this, curKey, newValue, type, true, true);
    this.saveChanges(curKey, newValCollection, oldValueCollection);
    this.properties[curKey] = newValCollection;
  };
}

function complexArrayFactoryGetter(defaultValue, curKey, type) {
  return function () {
    var curType = type({});

    if (!this.properties.hasOwnProperty(curKey)) {
      var defCollection = getObjectArray(this, curKey, defaultValue, curType, false);
      this.properties[curKey] = defCollection;
    }

    return this.properties[curKey];
  };
}

function complexArrayDefinedCallback(dFunc, curKey, type, prop) {
  /* tslint:disable no-function-expression */
  return function () {
    var newValue = [];

    for (var _i = 0; _i < arguments.length; _i++) {
      newValue[_i] = arguments[_i];
    }

    var keyString = this.propName ? this.getParentKey() + '.' + curKey + '-' : curKey + '-';

    switch (dFunc) {
      case 'push':
        for (var i = 0; i < newValue.length; i++) {
          Array.prototype[dFunc].apply(prop, [newValue[i]]);
          var model_1 = getArrayModel(keyString + (prop.length - 1), newValue[i], !this.controlParent, dFunc);
          this.serverDataBind(model_1, newValue[i], false, dFunc);
        }

        break;

      case 'pop':
        Array.prototype[dFunc].apply(prop);
        var model = getArrayModel(keyString + prop.length, null, !this.controlParent, dFunc);
        this.serverDataBind(model, {
          ejsAction: 'pop'
        }, false, dFunc);
        break;
    }

    return prop;
  };
  /* tslint:enable no-function-expression */
}

function getArrayModel(keyString, value, isControlParent, arrayFunction) {
  var modelObject = keyString;

  if (isControlParent) {
    modelObject = {};
    modelObject[keyString] = value;

    if (value && typeof value === 'object') {
      var action = 'ejsAction';
      modelObject[keyString][action] = arrayFunction;
    }
  }

  return modelObject;
}
/**
 * Method used to create property. General syntax below.
 * @param  {T} defaultValue? - Specifies the default value of property.
 * ```
 * @Property('TypeScript')
 * propertyName: Type;
 * ```
 * @private
 */


export function Property(defaultValue) {
  return function (target, key) {
    var propertyDescriptor = {
      set: propertySetter(defaultValue, key),
      get: propertyGetter(defaultValue, key),
      enumerable: true,
      configurable: true
    }; //new property creation

    Object.defineProperty(target, key, propertyDescriptor);
    addPropertyCollection(target, key, 'prop', defaultValue);
  };
}
/**
 * Method used to create complex property. General syntax below.
 * @param  {T} defaultValue - Specifies the default value of property.
 * @param  {Function} type - Specifies the class type of complex object.
 * ```
 * @Complex<Type>({},Type)
 * propertyName: Type;
 * ```
 * @private
 */

export function Complex(defaultValue, type) {
  return function (target, key) {
    var propertyDescriptor = {
      set: complexSetter(defaultValue, key, type),
      get: complexGetter(defaultValue, key, type),
      enumerable: true,
      configurable: true
    }; //new property creation

    Object.defineProperty(target, key, propertyDescriptor);
    addPropertyCollection(target, key, 'complexProp', defaultValue, type);
  };
}
/**
 * Method used to create complex Factory property. General syntax below.
 * @param  {Function} defaultType - Specifies the default value of property.
 * @param  {Function} type - Specifies the class factory type of complex object.
 * ```
 * @ComplexFactory(defaultType, factoryFunction)
 * propertyName: Type1 | Type2;
 * ```
 * @private
 */

export function ComplexFactory(type) {
  return function (target, key) {
    var propertyDescriptor = {
      set: complexFactorySetter({}, key, type),
      get: complexFactoryGetter({}, key, type),
      enumerable: true,
      configurable: true
    }; //new property creation

    Object.defineProperty(target, key, propertyDescriptor);
    addPropertyCollection(target, key, 'complexProp', {}, type);
  };
}
/**
 * Method used to create complex array property. General syntax below.
 * @param  {T[]} defaultValue - Specifies the default value of property.
 * @param  {Function} type - Specifies the class type of complex object.
 * ```
 * @Collection([], Type);
 * propertyName: Type;
 * ```
 * @private
 */

export function Collection(defaultValue, type) {
  return function (target, key) {
    var propertyDescriptor = {
      set: complexArraySetter(defaultValue, key, type),
      get: complexArrayGetter(defaultValue, key, type),
      enumerable: true,
      configurable: true
    }; //new property creation

    Object.defineProperty(target, key, propertyDescriptor);
    addPropertyCollection(target, key, 'colProp', defaultValue, type);
  };
}
/**
 * Method used to create complex factory array property. General syntax below.
 * @param  {T[]} defaultType - Specifies the default type of property.
 * @param  {Function} type - Specifies the class type of complex object.
 * ```
 * @Collection([], Type);
 * propertyName: Type;
 * ```
 * @private
 */

export function CollectionFactory(type) {
  return function (target, key) {
    var propertyDescriptor = {
      set: complexArrayFactorySetter([], key, type),
      get: complexArrayFactoryGetter([], key, type),
      enumerable: true,
      configurable: true
    }; //new property creation

    Object.defineProperty(target, key, propertyDescriptor);
    addPropertyCollection(target, key, 'colProp', {}, type);
  };
}
/**
 * Method used to create event property. General syntax below.
 * @param  {Function} defaultValue? - Specifies the default value of property.
 * @param  {boolean} isComplex? - Specifies the whether it is complex object.
 * ```
 * @Event(()=>{return true;})
 * ```
 * @private
 */

export function Event() {
  return function (target, key) {
    var eventDescriptor = {
      set: function set(newValue) {
        var oldValue = this.properties[key];

        if (oldValue !== newValue) {
          var finalContext = getParentContext(this, key);

          if (isUndefined(oldValue) === false) {
            finalContext.context.removeEventListener(finalContext.prefix, oldValue);
          }

          finalContext.context.addEventListener(finalContext.prefix, newValue);
          this.properties[key] = newValue;
        }
      },
      get: propertyGetter(undefined, key),
      enumerable: true,
      configurable: true
    };
    Object.defineProperty(target, key, eventDescriptor);
    addPropertyCollection(target, key, 'event');
  };
}
/**
 * NotifyPropertyChanges is triggers the call back when the property has been changed.
 *
 * ```
 *  @NotifyPropertyChanges
 * class DemoClass implements INotifyPropertyChanged {
 *
 *     @Property()
 *     property1: string;
 *
 *     dataBind: () => void;
 *
 *     constructor() { }
 *
 *     onPropertyChanged(newProp: any, oldProp: any) {
 *         // Called when property changed
 *     }
 * }
 * ```
 * @private
 */

export function NotifyPropertyChanges(classConstructor) {
  /** Need to code */
}
/**
 * Method  used to create the builderObject for the target component.
 * @private
 */

function addPropertyCollection(target, key, propertyType, defaultValue, type) {
  if (isUndefined(target.propList)) {
    target.propList = {
      props: [],
      complexProps: [],
      colProps: [],
      events: [],
      propNames: [],
      complexPropNames: [],
      colPropNames: [],
      eventNames: []
    };
  }
  /* tslint:disable no-any */


  target.propList[propertyType + 's'].push({
    propertyName: key,
    defaultValue: defaultValue,
    type: type
  });
  target.propList[propertyType + 'Names'].push(key);
  /* tslint:enable no-any */
}
/**
 * Returns an object containing the builder properties
 * @param {Function} component
 * @private
 */


function getBuilderProperties(component) {
  if (isUndefined(component.prototype.builderObject)) {
    component.prototype.builderObject = {
      properties: {},
      propCollections: [],
      add: function add() {
        this.isPropertyArray = true;
        this.propCollections.push(extend({}, this.properties, {}));
      }
    };
    var rex = /complex/;

    for (var _i = 0, _a = Object.keys(component.prototype.propList); _i < _a.length; _i++) {
      var key = _a[_i];

      var _loop_1 = function _loop_1(prop) {
        if (rex.test(key)) {
          component.prototype.builderObject[prop.propertyName] = function (value) {
            var childType = {};
            merge(childType, getBuilderProperties(prop.type));
            value(childType);
            var tempValue;

            if (!childType.isPropertyArray) {
              tempValue = extend({}, childType.properties, {});
            } else {
              tempValue = childType.propCollections;
            }

            this.properties[prop.propertyName] = tempValue;
            childType.properties = {};
            childType.propCollections = [];
            childType.isPropertyArray = false;
            return this;
          };
        } else {
          component.prototype.builderObject[prop.propertyName] = function (value) {
            this.properties[prop.propertyName] = value;
            return this;
          };
        }
      };

      for (var _b = 0, _c = component.prototype.propList[key]; _b < _c.length; _b++) {
        var prop = _c[_b];

        _loop_1(prop);
      }
    }
  }

  return component.prototype.builderObject;
}
/**
 * Method used to create builder for the components
 * @param {any} component -specifies the target component for which builder to be created.
 * @private
 */


export function CreateBuilder(component) {
  var builderFunction = function builderFunction(element) {
    this.element = element;
    return this;
  };

  var instanceFunction = function instanceFunction(element) {
    if (!builderFunction.prototype.hasOwnProperty('create')) {
      builderFunction.prototype = getBuilderProperties(component);

      builderFunction.prototype.create = function () {
        var temp = extend({}, {}, this.properties);
        this.properties = {};
        return new component(temp, this.element);
      };
    }

    return new builderFunction(element);
  };

  return instanceFunction;
}
/**
 * Returns parent options for the object
 * @param {Object} context
 * @param {string} prefix
 * @private
 */

function getParentContext(context, prefix) {
  if (context.hasOwnProperty('parentObj') === false) {
    return {
      context: context,
      prefix: prefix
    };
  } else {
    var curText = getValue('propName', context);

    if (curText) {
      prefix = curText + '-' + prefix;
    }

    return getParentContext(getValue('parentObj', context), prefix);
  }
}